import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Package, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDate } from '@/utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const FEED_TYPES = [
  { value: 'layer_mash', label: 'Layer Mash' },
  { value: 'starter', label: 'Starter' },
  { value: 'grower_mash', label: 'Grower Mash' }
];

const VENDORS = [
  { value: 'chickun', label: 'Chickun' },
  { value: 'peak', label: 'Peak' },
  { value: 'other', label: 'Other' }
];

const FeedInventory = () => {
  const [dailyBalance, setDailyBalance] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [receivedOpen, setReceivedOpen] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [receivedEntries, setReceivedEntries] = useState([]);
  const [usageEntries, setUsageEntries] = useState([]);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  // Check if user can manage feed inventory
  const canManageFeed = user.role === 'admin' || user.role === 'manager' || (user.role === 'staff' && user.can_manage_feed_inventory);

  const [receivedForm, setReceivedForm] = useState({
    date: new Date().toISOString().split('T')[0],
    feed_type: '',
    vendor: '',
    bags: '',
    notes: ''
  });

  const [usageForm, setUsageForm] = useState({
    date: new Date().toISOString().split('T')[0],
    feed_type: '',
    pen_number: '',
    bags: '',
    notes: ''
  });

  const [adjustmentForm, setAdjustmentForm] = useState({
    date: new Date().toISOString().split('T')[0],
    layer_mash_adjustment: '0',
    starter_adjustment: '0',
    grower_mash_adjustment: '0',
    reason: ''
  });

  useEffect(() => {
    fetchDailyBalance(selectedDate);
    fetchAllEntries();
  }, [selectedDate]);

  const fetchDailyBalance = async (date) => {
    try {
      const response = await axios.get(`${API}/feed/daily-balance/${date}`, getAuthHeader());
      setDailyBalance(response.data);
    } catch (error) {
      console.error('Failed to fetch daily balance');
    }
  };

  const fetchAllEntries = async () => {
    try {
      const [received, usage] = await Promise.all([
        axios.get(`${API}/feed/received`, getAuthHeader()),
        axios.get(`${API}/feed/usage`, getAuthHeader())
      ]);
      setReceivedEntries(received.data);
      setUsageEntries(usage.data);
    } catch (error) {
      console.error('Failed to fetch entries');
    }
  };

  const handleReceivedSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/feed/received`, {
        ...receivedForm,
        bags: parseInt(receivedForm.bags)
      }, getAuthHeader());
      toast.success('Feed received entry added');
      fetchDailyBalance(selectedDate);
      fetchAllEntries();
      setReceivedOpen(false);
      setReceivedForm({
        date: selectedDate,
        feed_type: '',
        vendor: '',
        bags: '',
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add entry');
    }
  };

  const handleUsageSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/feed/usage`, {
        ...usageForm,
        pen_number: parseInt(usageForm.pen_number),
        bags: parseInt(usageForm.bags)
      }, getAuthHeader());
      toast.success('Feed usage entry added');
      fetchDailyBalance(selectedDate);
      fetchAllEntries();
      setUsageOpen(false);
      setUsageForm({
        date: selectedDate,
        feed_type: '',
        pen_number: '',
        bags: '',
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add entry');
    }
  };

  const handleAdjustmentSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/feed/adjustments`, {
        ...adjustmentForm,
        layer_mash_adjustment: parseInt(adjustmentForm.layer_mash_adjustment),
        starter_adjustment: parseInt(adjustmentForm.starter_adjustment),
        grower_mash_adjustment: parseInt(adjustmentForm.grower_mash_adjustment)
      }, getAuthHeader());
      toast.success('Feed inventory adjusted');
      fetchDailyBalance(selectedDate);
      setAdjustOpen(false);
      setAdjustmentForm({
        date: selectedDate,
        layer_mash_adjustment: '0',
        starter_adjustment: '0',
        grower_mash_adjustment: '0',
        reason: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust inventory');
    }
  };

  const handleDeleteReceived = async (id) => {
    if (!window.confirm('Delete this feed received entry?')) return;
    try {
      await axios.delete(`${API}/feed/received/${id}`, getAuthHeader());
      toast.success('Entry deleted');
      fetchDailyBalance(selectedDate);
      fetchAllEntries();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleDeleteUsage = async (id) => {
    if (!window.confirm('Delete this feed usage entry?')) return;
    try {
      await axios.delete(`${API}/feed/usage/${id}`, getAuthHeader());
      toast.success('Entry deleted');
      fetchDailyBalance(selectedDate);
      fetchAllEntries();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const getFeedLabel = (value) => FEED_TYPES.find(f => f.value === value)?.label || value;
  const getVendorLabel = (value) => VENDORS.find(v => v.value === value)?.label || value;

  return (
    <div className="space-y-6" data-testid="feed-inventory-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Feed Inventory
          </h1>
          <p className="text-lg text-slate-600 mt-2">Track feed received, usage and daily balance</p>
        </div>
        {canManageFeed && (
          <div className="flex gap-2">
            <Button data-testid="add-received-button" onClick={() => setReceivedOpen(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Received
            </Button>
            <Button data-testid="add-usage-button" onClick={() => setUsageOpen(true)} variant="outline" className="border-amber-600 text-amber-600 hover:bg-amber-50">
              <Package className="w-4 h-4 mr-2" />
              Record Usage
            </Button>
          </div>
        )}
      </div>

      {/* Date Selector and Adjustment Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Label>View Date:</Label>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-48"
              data-testid="date-selector"
            />
            <span className="text-sm font-medium text-slate-600 bg-slate-100 px-3 py-2 rounded">
              {formatDate(selectedDate)}
            </span>
          </div>
        </div>
        {canManageFeed && (user.role === 'admin' || user.role === 'manager') && (
          <Button onClick={() => setAdjustOpen(true)} variant="outline" className="border-purple-600 text-purple-600 hover:bg-purple-50">
            <Edit className="w-4 h-4 mr-2" />
            Adjust Inventory
          </Button>
        )}
      </div>

      {/* Daily Balance Table */}
      {dailyBalance && (
        <div className="card p-6">
          <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Daily Feed Balance - {formatDate(selectedDate)}
          </h3>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-3 px-2 font-semibold">Category</th>
                  <th className="text-center py-3 px-2 font-semibold">Layer Mash</th>
                  <th className="text-center py-3 px-2 font-semibold">Starter</th>
                  <th className="text-center py-3 px-2 font-semibold">Grower Mash</th>
                </tr>
              </thead>
              <tbody>
                {/* Opening Balance */}
                <tr className="border-b border-slate-100 bg-slate-50">
                  <td className="py-3 px-2 font-medium text-slate-700">Opening Balance (bags)</td>
                  <td className="text-center py-3 px-2">{dailyBalance.opening_balance.layer_mash}</td>
                  <td className="text-center py-3 px-2">{dailyBalance.opening_balance.starter}</td>
                  <td className="text-center py-3 px-2">{dailyBalance.opening_balance.grower_mash}</td>
                </tr>

                {/* Received by Vendor */}
                <tr className="border-b border-slate-100">
                  <td colSpan="4" className="py-2 px-2 font-semibold text-emerald-700 bg-emerald-50">
                    + Feed Received (by Vendor)
                  </td>
                </tr>
                {VENDORS.map(vendor => (
                  <tr key={vendor.value} className="border-b border-slate-100">
                    <td className="py-2 px-2 pl-6 text-slate-600">{vendor.label}</td>
                    <td className="text-center py-2 px-2 text-emerald-600">
                      {dailyBalance.received_by_vendor.layer_mash[vendor.value] > 0 ? `+${dailyBalance.received_by_vendor.layer_mash[vendor.value]}` : '-'}
                    </td>
                    <td className="text-center py-2 px-2 text-emerald-600">
                      {dailyBalance.received_by_vendor.starter[vendor.value] > 0 ? `+${dailyBalance.received_by_vendor.starter[vendor.value]}` : '-'}
                    </td>
                    <td className="text-center py-2 px-2 text-emerald-600">
                      {dailyBalance.received_by_vendor.grower_mash[vendor.value] > 0 ? `+${dailyBalance.received_by_vendor.grower_mash[vendor.value]}` : '-'}
                    </td>
                  </tr>
                ))}
                <tr className="border-b border-slate-100 bg-emerald-50">
                  <td className="py-2 px-2 pl-6 font-medium text-emerald-700">Total Received</td>
                  <td className="text-center py-2 px-2 font-semibold text-emerald-700">+{dailyBalance.received_totals.layer_mash}</td>
                  <td className="text-center py-2 px-2 font-semibold text-emerald-700">+{dailyBalance.received_totals.starter}</td>
                  <td className="text-center py-2 px-2 font-semibold text-emerald-700">+{dailyBalance.received_totals.grower_mash}</td>
                </tr>

                {/* Usage */}
                <tr className="border-b border-slate-100 bg-red-50">
                  <td className="py-3 px-2 font-medium text-red-700">- Feed Used (to Pens)</td>
                  <td className="text-center py-3 px-2 font-semibold text-red-600">-{dailyBalance.usage_totals.layer_mash}</td>
                  <td className="text-center py-3 px-2 font-semibold text-red-600">-{dailyBalance.usage_totals.starter}</td>
                  <td className="text-center py-3 px-2 font-semibold text-red-600">-{dailyBalance.usage_totals.grower_mash}</td>
                </tr>

                {/* Adjustments (if any) */}
                {(dailyBalance.adjustments.layer_mash !== 0 || dailyBalance.adjustments.starter !== 0 || dailyBalance.adjustments.grower_mash !== 0) && (
                  <tr className="border-b border-slate-100 bg-amber-50">
                    <td className="py-3 px-2 font-medium text-amber-700">Adjustments</td>
                    <td className="text-center py-3 px-2 font-semibold text-amber-600">
                      {dailyBalance.adjustments.layer_mash > 0 ? '+' : ''}{dailyBalance.adjustments.layer_mash}
                    </td>
                    <td className="text-center py-3 px-2 font-semibold text-amber-600">
                      {dailyBalance.adjustments.starter > 0 ? '+' : ''}{dailyBalance.adjustments.starter}
                    </td>
                    <td className="text-center py-3 px-2 font-semibold text-amber-600">
                      {dailyBalance.adjustments.grower_mash > 0 ? '+' : ''}{dailyBalance.adjustments.grower_mash}
                    </td>
                  </tr>
                )}

                {/* Closing Balance */}
                <tr className="border-t-2 border-slate-300 bg-gradient-to-r from-purple-50 to-blue-50">
                  <td className="py-4 px-2 font-bold text-lg text-slate-800">Closing Balance (bags)</td>
                  <td className="text-center py-4 px-2 font-bold text-xl text-purple-700">{dailyBalance.closing_balance.layer_mash}</td>
                  <td className="text-center py-4 px-2 font-bold text-xl text-purple-700">{dailyBalance.closing_balance.starter}</td>
                  <td className="text-center py-4 px-2 font-bold text-xl text-purple-700">{dailyBalance.closing_balance.grower_mash}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-sm text-slate-500 mt-4">
            Closing balance carries forward to next day's opening balance
          </p>
        </div>
      )}

      {/* Summary Cards */}
      {dailyBalance && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card p-6 bg-gradient-to-br from-amber-50 to-white border-l-4 border-amber-500">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Layer Mash Stock</p>
            <p className="text-3xl font-bold text-slate-900 mt-2">{dailyBalance.closing_balance.layer_mash} bags</p>
            <p className="text-xs text-slate-500 mt-1">Available in storage</p>
          </div>
          <div className="card p-6 bg-gradient-to-br from-blue-50 to-white border-l-4 border-blue-500">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Starter Stock</p>
            <p className="text-3xl font-bold text-slate-900 mt-2">{dailyBalance.closing_balance.starter} bags</p>
            <p className="text-xs text-slate-500 mt-1">Available in storage</p>
          </div>
          <div className="card p-6 bg-gradient-to-br from-emerald-50 to-white border-l-4 border-emerald-500">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Grower Mash Stock</p>
            <p className="text-3xl font-bold text-slate-900 mt-2">{dailyBalance.closing_balance.grower_mash} bags</p>
            <p className="text-xs text-slate-500 mt-1">Available in storage</p>
          </div>
        </div>
      )}

      {/* Tabs for Recent Entries */}
      <Tabs defaultValue="received" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="received" data-testid="received-tab">Recent Feed Received</TabsTrigger>
          <TabsTrigger value="usage" data-testid="usage-tab">Recent Feed Usage</TabsTrigger>
        </TabsList>

        <TabsContent value="received">
          <div className="card">
            <div className="overflow-x-auto">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Feed Type</th>
                    <th>Vendor</th>
                    <th className="text-right">Bags</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="received-table-body">
                  {receivedEntries.slice(0, 20).map((entry) => (
                    <tr key={entry.id}>
                      <td>{formatDate(entry.date)}</td>
                      <td>{getFeedLabel(entry.feed_type)}</td>
                      <td>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100">
                          {getVendorLabel(entry.vendor)}
                        </span>
                      </td>
                      <td className="text-right font-semibold text-emerald-600">+{entry.bags}</td>
                      <td className="max-w-xs truncate">{entry.notes || '-'}</td>
                      <td>
                        <button
                          data-testid={`delete-received-${entry.id}`}
                          onClick={() => handleDeleteReceived(entry.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {receivedEntries.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  No feed received entries yet.
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="usage">
          <div className="card">
            <div className="overflow-x-auto">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Feed Type</th>
                    <th>Pen</th>
                    <th className="text-right">Bags Used</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="usage-table-body">
                  {usageEntries.slice(0, 20).map((entry) => (
                    <tr key={entry.id}>
                      <td>{formatDate(entry.date)}</td>
                      <td>{getFeedLabel(entry.feed_type)}</td>
                      <td>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          Pen {entry.pen_number}
                        </span>
                      </td>
                      <td className="text-right font-semibold text-red-600">-{entry.bags}</td>
                      <td className="max-w-xs truncate">{entry.notes || '-'}</td>
                      <td>
                        <button
                          data-testid={`delete-usage-${entry.id}`}
                          onClick={() => handleDeleteUsage(entry.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {usageEntries.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  No feed usage entries yet.
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Feed Received Dialog */}
      <Dialog open={receivedOpen} onOpenChange={setReceivedOpen}>
        <DialogContent data-testid="received-dialog">
          <DialogHeader>
            <DialogTitle>Add Feed Received</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReceivedSubmit} className="space-y-4">
            <div>
              <Label htmlFor="rec-date">Date</Label>
              <Input
                id="rec-date"
                type="date"
                data-testid="received-date-input"
                value={receivedForm.date}
                onChange={(e) => setReceivedForm({ ...receivedForm, date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="rec-feed-type">Feed Type</Label>
              <Select onValueChange={(value) => setReceivedForm({ ...receivedForm, feed_type: value })} value={receivedForm.feed_type}>
                <SelectTrigger data-testid="received-feed-type-select">
                  <SelectValue placeholder="Select feed type" />
                </SelectTrigger>
                <SelectContent>
                  {FEED_TYPES.map(ft => (
                    <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="rec-vendor">Vendor</Label>
              <Select onValueChange={(value) => setReceivedForm({ ...receivedForm, vendor: value })} value={receivedForm.vendor}>
                <SelectTrigger data-testid="received-vendor-select">
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  {VENDORS.map(v => (
                    <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="rec-bags">Number of Bags</Label>
              <Input
                id="rec-bags"
                type="number"
                data-testid="received-bags-input"
                value={receivedForm.bags}
                onChange={(e) => setReceivedForm({ ...receivedForm, bags: e.target.value })}
                min="1"
                required
              />
            </div>
            <div>
              <Label htmlFor="rec-notes">Notes (Optional)</Label>
              <Textarea
                id="rec-notes"
                data-testid="received-notes-input"
                value={receivedForm.notes}
                onChange={(e) => setReceivedForm({ ...receivedForm, notes: e.target.value })}
                placeholder="Delivery details, invoice number, etc."
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" data-testid="received-submit-button" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                Add Entry
              </Button>
              <Button type="button" variant="outline" onClick={() => setReceivedOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Record Feed Usage Dialog */}
      <Dialog open={usageOpen} onOpenChange={setUsageOpen}>
        <DialogContent data-testid="usage-dialog">
          <DialogHeader>
            <DialogTitle>Record Feed Usage to Pen</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUsageSubmit} className="space-y-4">
            <div>
              <Label htmlFor="use-date">Date</Label>
              <Input
                id="use-date"
                type="date"
                data-testid="usage-date-input"
                value={usageForm.date}
                onChange={(e) => setUsageForm({ ...usageForm, date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="use-pen">Pen Number</Label>
              <Select onValueChange={(value) => setUsageForm({ ...usageForm, pen_number: value })} value={usageForm.pen_number}>
                <SelectTrigger data-testid="usage-pen-select">
                  <SelectValue placeholder="Select pen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Pen 1</SelectItem>
                  <SelectItem value="2">Pen 2</SelectItem>
                  <SelectItem value="3">Pen 3</SelectItem>
                  <SelectItem value="4">Pen 4</SelectItem>
                  <SelectItem value="5">Pen 5</SelectItem>
                  <SelectItem value="6">Pen 6</SelectItem>
                  <SelectItem value="7">Brooding Pen</SelectItem>
                  <SelectItem value="8">Grower Pen</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="use-feed-type">Feed Type</Label>
              <Select onValueChange={(value) => setUsageForm({ ...usageForm, feed_type: value })} value={usageForm.feed_type}>
                <SelectTrigger data-testid="usage-feed-type-select">
                  <SelectValue placeholder="Select feed type" />
                </SelectTrigger>
                <SelectContent>
                  {FEED_TYPES.map(ft => (
                    <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="use-bags">Bags Used</Label>
              <Input
                id="use-bags"
                type="number"
                data-testid="usage-bags-input"
                value={usageForm.bags}
                onChange={(e) => setUsageForm({ ...usageForm, bags: e.target.value })}
                min="1"
                required
              />
            </div>
            <div>
              <Label htmlFor="use-notes">Notes (Optional)</Label>
              <Textarea
                id="use-notes"
                data-testid="usage-notes-input"
                value={usageForm.notes}
                onChange={(e) => setUsageForm({ ...usageForm, notes: e.target.value })}
                placeholder="Morning feeding, special notes, etc."
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" data-testid="usage-submit-button" className="flex-1 bg-amber-600 hover:bg-amber-700">
                Record Usage
              </Button>
              <Button type="button" variant="outline" onClick={() => setUsageOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Adjustment Dialog */}
      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent data-testid="adjustment-dialog">
          <DialogHeader>
            <DialogTitle>Adjust Feed Inventory - {formatDate(selectedDate)}</DialogTitle>
            <p className="text-sm text-slate-600">Enter positive or negative numbers to adjust inventory to match physical count</p>
          </DialogHeader>
          <form onSubmit={handleAdjustmentSubmit} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="adj-layer">Layer Mash</Label>
                <Input
                  id="adj-layer"
                  type="number"
                  data-testid="adj-layer-input"
                  value={adjustmentForm.layer_mash_adjustment}
                  onChange={(e) => setAdjustmentForm({ ...adjustmentForm, layer_mash_adjustment: e.target.value })}
                  placeholder="e.g., -2 or +5"
                />
              </div>
              <div>
                <Label htmlFor="adj-starter">Starter</Label>
                <Input
                  id="adj-starter"
                  type="number"
                  data-testid="adj-starter-input"
                  value={adjustmentForm.starter_adjustment}
                  onChange={(e) => setAdjustmentForm({ ...adjustmentForm, starter_adjustment: e.target.value })}
                  placeholder="e.g., -2 or +5"
                />
              </div>
              <div>
                <Label htmlFor="adj-grower">Grower Mash</Label>
                <Input
                  id="adj-grower"
                  type="number"
                  data-testid="adj-grower-input"
                  value={adjustmentForm.grower_mash_adjustment}
                  onChange={(e) => setAdjustmentForm({ ...adjustmentForm, grower_mash_adjustment: e.target.value })}
                  placeholder="e.g., -2 or +5"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="adj-reason">Reason for Adjustment</Label>
              <Textarea
                id="adj-reason"
                data-testid="adj-reason-input"
                value={adjustmentForm.reason}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, reason: e.target.value })}
                placeholder="Physical count adjustment, spillage, etc."
                required
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" data-testid="adjustment-submit-button" className="flex-1 bg-purple-600 hover:bg-purple-700">
                Apply Adjustment
              </Button>
              <Button type="button" variant="outline" onClick={() => setAdjustOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeedInventory;
